// Libs
import { Redirect, Route, Switch } from "react-router";
import isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";
import React from "react";

// Components
import ErrorBoundary from "components/ErrorBoundary";
import PageLoader from "components/loaders/PageLoader";
import RenderIf from "components/RenderIf";

// Utils
import {
  canAccessClaimsPage,
  canAccessDocumentsPage,
  canAccessPeoplePage,
  canAccessAnalyticsPage
} from "utils/authorization/authorization";

// Constants
import { ROUTE_CONFIGS, ROUTES } from "constants/routing";

// Statically Imported Pages
import NotFoundPage from "pages/errors/NotFoundPage";
import Logout from "pages/logout";
import AboutPage from "pages/about";
import SpringbukAnalytics from "pages/analytics/SpringbukAnalytics";

// Dynamically Imported Pages
const AccountPage = React.lazy(() =>
  import(/* webpackChunkName: "AccountPage" */ "pages/account/AccountPage")
);
const DocumentsPage = React.lazy(() =>
  import(/* webpackChunkName: "DocumentsPage" */ "pages/documents/DocumentsPage")
);

const ClaimsPage = React.lazy(() =>
  import(/* webpackChunkName: "ClaimsPage" */ "pages/claims/ClaimsPage")
);

const PeoplePage = React.lazy(() =>
  import(/* webpackChunkName: "PeoplePage" */ "pages/people/PeoplePage")
);

export default function PageRouter({ menuItems, role }) {
  return (
    <ErrorBoundary>
      <React.Suspense fallback={<PageLoader isLoading={true} />}>
        {/* Redirect to the first available menu item */}
        <RenderIf test={role && !isEmpty(menuItems)}>
          <Route
            path={[ROUTES.BASE, ROUTES.LANDING]}
            render={() => <Redirect to={menuItems[0].path} />}
            exact={true}
            strict={true}
          />
        </RenderIf>

        <Switch>
          {/* People Page Route */}
          {canAccessPeoplePage(role) && (
            <Route
              {...ROUTE_CONFIGS.PEOPLE}
              render={props => <PeoplePage {...props} />}
              exact={false}
            />
          )}

          {/* Documents Page Route */}
          {canAccessDocumentsPage(role) && (
            <Route
              {...ROUTE_CONFIGS.DOCUMENTS}
              render={props => <DocumentsPage {...props} />}
              exact={false}
            />
          )}

          {/* Claims Page Route */}
          {canAccessClaimsPage(role) && (
            <Route
              {...ROUTE_CONFIGS.CLAIMS}
              render={props => <ClaimsPage {...props} />}
              exact={false}
            />
          )}

          {canAccessAnalyticsPage(role) && (
            <Route
              {...ROUTE_CONFIGS.ANALYTICS}
              render={() => <SpringbukAnalytics />}
              exact={false}
            />
          )}

          {/* Accounts Page Route */}
          <Route
            {...ROUTE_CONFIGS.ACCOUNT}
            render={props => <AccountPage {...props} role={role} />}
            exact={false}
          />

          <Route {...ROUTE_CONFIGS.ABOUT} render={props => <AboutPage {...props} />} />

          {/* Logout Handler */}
          <Route {...ROUTE_CONFIGS.LOGOUT} render={props => <Logout {...props} />} />

          {/* Redirect to "/" when user lands on "/login" */}
          <Route
            {...ROUTE_CONFIGS.LOGIN}
            render={() => {
              window.location.href = "/";
              return null;
            }}
          />

          {/* Catch-all route */}
          <Route render={props => <NotFoundPage {...props} />} />
        </Switch>
      </React.Suspense>
    </ErrorBoundary>
  );
}

PageRouter.propTypes = {
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired
    }).isRequired
  ).isRequired,
  role: PropTypes.string
};
